import { createContext, useContext, useCallback } from 'react'
import toast, { Toaster } from 'react-hot-toast'

export const ToastContext = createContext(null)

export const ToastProvider = ({ children }) => {
  // Success toast
  const success = useCallback((message, options = {}) => {
    return toast.success(message, {
      duration: 4000,
      position: 'top-right',
      ...options,
    })
  }, [])

  // Error toast
  const error = useCallback((message, options = {}) => {
    return toast.error(message, {
      duration: 5000,
      position: 'top-right',
      ...options,
    })
  }, [])

  // Warning toast
  const warning = useCallback((message, options = {}) => {
    return toast(message, {
      icon: '⚠️',
      duration: 4000,
      position: 'top-right',
      ...options,
    })
  }, [])

  // Info toast
  const info = useCallback((message, options = {}) => {
    return toast(message, {
      icon: 'ℹ️',
      duration: 4000,
      position: 'top-right',
      ...options,
    })
  }, [])

  // Loading toast
  const loading = useCallback((message, options = {}) => {
    return toast.loading(message, {
      position: 'top-right',
      ...options,
    })
  }, [])

  // Promise toast (for async operations)
  const promise = useCallback((promise, messages, options = {}) => {
    return toast.promise(
      promise,
      {
        loading: messages.loading || 'Chargement...',
        success: messages.success || 'Succès!',
        error: messages.error || 'Erreur',
      },
      {
        position: 'top-right',
        ...options,
      }
    )
  }, [])

  // Dismiss a toast
  const dismiss = useCallback((toastId) => {
    return toast.dismiss(toastId)
  }, [])

  // Dismiss all toasts
  const dismissAll = useCallback(() => {
    return toast.dismiss()
  }, [])

  const value = {
    success,
    error,
    warning,
    info,
    loading,
    promise,
    dismiss,
    dismissAll,
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster
        position="top-right"
        reverseOrder={false}
        gutter={8}
        toastOptions={{
          // Default options
          duration: 4000,
          style: {
            background: '#fff',
            color: '#363636',
            fontSize: '14px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
          // Success
          success: {
            style: {
              background: '#e8f5e9',
              color: '#2e7d32',
            },
            iconTheme: {
              primary: '#4caf50',
              secondary: '#fff',
            },
          },
          // Error
          error: {
            style: {
              background: '#ffebee',
              color: '#c62828',
            },
            iconTheme: {
              primary: '#f44336',
              secondary: '#fff',
            },
          },
        }}
      />
    </ToastContext.Provider>
  )
}

/**
 * Hook to access toast context
 * @returns {Object} Toast context value
 * @throws {Error} If used outside of ToastProvider
 */
export function useToast() {
  const context = useContext(ToastContext)
  
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  
  return context
}

// Export both named and default for compatibility
export default useToast


